<template>
  <v-form>
    <template v-if="!registrationMode && currentData">
      <v-col>
        <v-row>
          <v-chip
            :color="isMyself ? 'primary': 'warning'"
            variant="flat"
            density="compact"
            class="mb-4 mr-1"
          >
            {{ isMyself ? $t('User.MyAccount') : $t('User.OtherAccount') }}
          </v-chip>
          <div class="caption text-grey">
            {{ isMyself ? $t('User.EditMyAccountMessage') : $t('User.EditOtherAccountMessage') }}
          </div>
        </v-row>
      </v-col>
    </template>
    <TwoColumnFormLayout :reverse="reverse">
      <!-- 姓 -->
      <template #first>
        <TextInput
          v-model="formData.last_name"
          :field-key="fieldKey('last_name')"
          :label="$t('User.LastName')"
          required
          @change="copyLastName()"
        />
      </template>
      <!-- 名 -->
      <template #second>
        <TextInput
          v-model="formData.first_name"
          :field-key="fieldKey('first_name')"
          :label="$t('User.FirstName')"
          required
          @change="copyFirstName()"
        />
      </template>
    </TwoColumnFormLayout>

    <TwoColumnFormLayout :reverse="reverse">
      <!-- 姓（英字） -->
      <template #first>
        <TextInput
          v-model="formData.last_name_en"
          :field-key="fieldKey('last_name_en')"
          :label="$t('User.LastNameAlphabet')"
          required
          :hint="$t('User.Hint.LastNameAlphabet')"
          ime-off
        />
      </template>
      <!-- 名（英字） -->
      <template #second>
        <TextInput
          v-model="formData.first_name_en"
          :field-key="fieldKey('first_name_en')"
          :label="$t('User.FirstNameAlphabet')"
          required
          :hint="$t('User.Hint.FirstNameAlphabet')"
          ime-off
        />
      </template>
    </TwoColumnFormLayout>

    <TwoColumnFormLayout>
      <!-- Email -->
      <template #first>
        <v-alert
          v-if="currentData"
          v-show="currentData.email !== formData.email"
          type="info"
          class="py-2"
        >
          {{ $t('User.FormInfo.IfChangeEmailWillBeLogout') }}
        </v-alert>
        <template v-if="preEmail">
          <TextInput
            :label="$t('User.EmailAddress')"
            required
            disabled
            :model-value="preEmail"
          />
        </template>
        <template v-else>
          <TextInput
            v-model="formData.email"
            :field-key="fieldKey('email')"
            :label="$t('User.EmailAddress')"
            required
            :disabled="!login.profile.is_admin && !registrationMode"
            autocomplete="off"
            @change="changedEmailFlag=true"
          />
        </template>
        <div
          v-if="!login.profile.is_admin && !registrationMode"
          class="caption text-grey"
          style="margin-top: -20px;"
        >
          * {{ $t('User.FormInfo.CanChangeOnlyAdmin') }}
        </div>
      </template>

      <!-- password -->
      <!-- -- レジストレーション時 / 管理者が他人をプロファイルで表示しており、かつ、パスワード自動設定OFF -->
      <template #second>
        <template v-if="registrationMode || isCreateEditForOtherUserFromAdmin && formData.set_auto_password === false">
          <PasswordInput
            v-model="formData.password"
            :field-key="fieldKey('password')"
            :label="$t('User.Password')"
            required
          />
        </template>
        <!-- -- 自分自身をプロファイルで表示している時 -->
        <template v-if="!registrationMode && isMyself">
          <ChangePasswordDialog />
        </template>
      </template>
    </TwoColumnFormLayout>

    <!-- 初期パスワード自動設定 -->
    <!-- -- 管理者が他人をプロファイルで表示している時 -->
    <div v-show="isCreateEditForOtherUserFromAdmin && formData.email.indexOf('@') >= 0">
      <CheckBoxInput
        v-model="formData.set_auto_password"
        :label="$t('User.SetAutoPassword', {email: formData.email})"
        :field-key="fieldKey('set_auto_password')"
        class="mt-0"
      />
    </div>

    <!--  info about social account  -->
    <TwoColumnFormLayout>
      <template #first>
        <div>
          <template v-if="registrationMode || isMyself || (isCreateEditForOtherUserFromAdmin && formData.email.indexOf('@') >= 0)">
            <!-- checkbox to allow/disallow a user to use social account -->
            <v-checkbox
              v-if="login.profile.is_admin"
              v-model="formData.allow_social_account"
              :label="$t('SocialAccounts.allowCheckboxLabel')"
              hide-details
              color="secondary"
              :disabled="!login.profile.is_admin && !registrationMode"
            />
            <!--  state of linked / unlinked  -->
            <template v-if="!registrationMode">
              <SocialAccountState
                v-if="(login.profile.is_admin && formData.allow_social_account) || (!login.profile.is_admin)"
                :accounts="formData.social_accounts"
                :read-only="!isMyself"
                @link-account="applySocialAccounts"
              />
              <div
                v-if="!registrationMode && !login.profile.is_admin && !currentData.allow_social_account"
                class="caption text-grey"
              >
                * {{ $t('SocialAccounts.NotAuthorizedByAdministrator') }}
              </div>
            </template>
            <!--  show alert if disallow a user to use social account  -->
            <v-alert v-if="currentData && currentData.allow_social_account && !formData.allow_social_account" type="info" class="ma-3">
              {{ $t('SocialAccounts.UserWillBeLoggedOut') }}
            </v-alert>
          </template>
        </div>
      </template>
    </TwoColumnFormLayout>

    <TwoColumnFormLayout>
      <template #first>
        <!-- 言語 -->
        <SelectLanguage
          v-model="formData.language"
          :field-key="fieldKey('language')"
          required
        />
      </template>
      <template #second>
        <!-- タイムゾーン -->
        <SelectTimezone
          v-model="formData.timezone"
          :field-key="fieldKey('timezone')"
          required
        />
      </template>
    </TwoColumnFormLayout>

    <!-- 権限 -->
    <div
      v-if="!registrationMode && login.profile.is_admin"
      class="mb-3"
    >
      <SelectItems
        v-model="formData.roles"
        :field-key="fieldKey('roles')"
        :label="$t('User.AdditionalRoles')"
        :items="selectRoleList"
        item-text="name"
        item-value="id"
        if-primary-color-id="admin"
      />
      <v-fade-transition>
        <div style="margin-top: -20px;">
          <div v-show="isSelectingAdmin">
            {{ $t('User.FormInfo.Admin') }}
          </div>
          <v-alert
            v-model="isRemovingAdmin"
            type="error"
            class="mb-1"
          >
            {{ $t('User.FormInfo.DisableMyselfAdminAlert') }}
          </v-alert>
        </div>
      </v-fade-transition>
    </div>

    <!-- 所属ユーザーグループ -->
    <div
      v-if="!registrationMode && login.profile.is_admin"
      class="mb-3"
    >
      <SelectItems
        v-model="formData.user_groups"
        :field-key="fieldKey('user_groups')"
        :label="$t('UserGroup.UserGroups')"
        get-items-api="user-groups/"
        item-text="user_group_name"
        item-value="id"
      />
    </div>

    <!-- アカウント無効 -->
    <div v-if="!registrationMode">
      <v-divider />
      <CheckBoxInput
        v-model="formData.is_active"
        :field-key="fieldKey('is_active')"
        :label="$t('User.DisableAccount')"
        not
      />
      <v-fade-transition>
        <div
          v-if="!formData.is_active"
          style="margin-top: -20px;"
        >
          <div>
            {{ $t('User.FormInfo.DisableAccount') }}
          </div>
          <v-alert
            type="error"
            :model-value="isMyself"
            class="mb-1"
          >
            {{ $t('User.FormInfo.DisableMyselfAccountAlert') }}
          </v-alert>
        </div>
      </v-fade-transition>
    </div>
  </v-form>
</template>

<script>
import SelectLanguage from '../parts/inputs/SelectLanguageInput'
import SelectTimezone from '../parts/inputs/SelectTimezoneInput'
import TextInput from '@/components/parts/inputs/TextInput'
import PasswordInput from '@/components/parts/inputs/PasswordInput'
import TwoColumnFormLayout from '@/components/forms/layouts/TwoColumnFormLayout'
import FormMixin from './FormMixin'
import CheckBoxInput from '../../components/parts/inputs/CheckBoxInput'
import SelectItems from '../../components/parts/inputs/SelectItems'
import ChangePasswordDialog from '../../components/dialogs/ChangePasswordDialog'
import * as utils from '../../library/utils'
import SocialAccountState from '../parts/SocialAccountState.vue'

export default {
  components: {
    SocialAccountState,
    ChangePasswordDialog,
    SelectItems,
    CheckBoxInput,
    TwoColumnFormLayout,
    PasswordInput,
    TextInput,
    SelectLanguage,
    SelectTimezone
  },
  mixins: [FormMixin],
  props: {
    preEmail: {
        type: String,
        default: ''
      },  // Emailを変更不可で強制する場合、この値に強制するEMailを渡す
    registrationMode: Boolean,
    selectedGroups: {
        type: Array,
        default () {
          return []
        }
      },  // あらかじめグループを選択済みにする（新規作成時のみ値が入る）
    currentData: {
      type: Object,
      default () {
        return null
      }
    }
  },
  emits: ['link-account'],
  data: () => ({
    formData: {
      first_name: '',
      last_name: '',
      first_name_en: '',
      last_name_en: '',
      email: '',
      password: null,
      language: '',
      timezone: '',
      set_auto_password: false,
      roles: [],
      is_active: true,
      user_groups: [],
      allow_social_account: false,
      social_accounts: []
    },
    selectRoleList: []
  }),
  computed: {
    isMyself () {
      if (this.currentData) {
        if (this.login.profile) {
          return this.currentData.id === this.login.profile.id
        } else {
          return false
        }
      } else {
        return false
      }
    },
    isCreateEditForOtherUserFromAdmin () {
      return !this.registrationMode && !this.isMyself && this.login.profile.is_admin
    },
    isRemovingAdmin () {
      return this.isMyself && this.login.profile.is_admin && this.formData.roles.indexOf('admin') === -1
    },
    isSelectingAdmin () {
      return this.formData.roles.indexOf('admin') >= 0
    }
  },
  watch: {
    // 英字は強制的に大文字
    'formData.last_name_en' () {
      this.formData.last_name_en = this.formData.last_name_en.toUpperCase()
    },
    // 英字は強制的に大文字
    'formData.first_name_en' () {
      this.formData.first_name_en = this.formData.first_name_en.toUpperCase()
    }
  },
  created () {
    this.reset()
    this.selectRoleList = [
      { id: 'admin', name: this.$t('User.Role.admin') }
    ]
    if (this.registrationMode) {
      this.formData.password = ''
    }
    this.applySelectedGroups()
    this.applyCurrentData()
  },
  methods: {
    reset () {
      // data初期化
      Object.assign(this.$data.formData, this.$options.data().formData)
      this.formData.language = this.$i18n.locale
      this.formData.timezone = utils.expectationTimeZone()
    },
    isAlphabet (str) {
      return (!str.match(/[^a-z]/gi)) && str !== ''
    },
    copyLastName () {
      if (this.isAlphabet(this.formData.last_name)) {
        this.formData.last_name_en = this.formData.last_name
      }
    },
    copyFirstName () {
      if (this.isAlphabet(this.formData.first_name)) {
        this.formData.first_name_en = this.formData.first_name
      }
    },
    applySelectedGroups () {
      if (this.selectedGroups && this.selectedGroups.length === 1) {
        this.formData['user_groups'] = this.selectedGroups
      }
    },
    applySocialAccounts (data) {
      /**
       * update the into about social accounts to formData
       *
       * data(object): info about the social account, containing provider, email and social_id
       */
      // check if provider already exists
      const providers = this.currentData.social_accounts.map((account) => {
        return account.provider
      })
      if (providers.includes(data.provider)) {
        // raise error
        console.error('provider already exists')
        return
      }

      // update the form data
      this.formData.social_accounts = [...this.formData.social_accounts, { provider: data.provider, email: data.email, social_id: data.social_id }]
    }
  }
}
</script>
